import { Link } from "react-router-dom";
import { SEO, breadcrumbLd } from "@/lib/seo";
import { COMPANY, CITIES } from "@/lib/constants";
import Reveal, { SectionHead } from "@/components/Reveal";

const ONas = () => (
  <>
    <SEO
      title="O nas – AD Brikman Usługi Brukarskie | Brukarz Łęczna, Lublin"
      description="AD Brikman Usługi Brukarskie z Łęcznej — perfekcjonizm, profesjonalizm i rzetelność. Kostka brukowa, brukarstwo artystyczne, pomoc w projekcie i doborze materiałów. Łęczna, Lublin, Świdnik i okolice."
      path="/o-nas"
      jsonLd={breadcrumbLd([{ label: "Strona główna", path: "/" }, { label: "O nas", path: "/o-nas" }])}
    />

    <section className="grain relative overflow-hidden bg-ink pt-36 pb-16 lg:pt-44">
      <div className="paver-bg absolute inset-0 opacity-60" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8">
        <Reveal>
          <p className="eyebrow mb-5">O nas</p>
        </Reveal>
        <Reveal delay={0.05}>
          <h1 className="font-display max-w-4xl text-5xl font-black uppercase leading-[0.95] tracking-tight text-pearl sm:text-7xl" data-testid="o-nas-title">
            Z Łęcznej. <span className="text-amber">Z pasją do bruku.</span>
          </h1>
        </Reveal>
      </div>
    </section>

    <section className="mx-auto grid max-w-7xl gap-14 px-4 py-20 sm:px-8 lg:grid-cols-2 lg:items-center" data-testid="o-nas-story">
      <Reveal>
        <div className="notch overflow-hidden">
          <img src="/photos/photo-07.jpg" alt="Ekipa AD Brikman przy pracy — układanie kostki brukowej" loading="lazy" className="h-[460px] w-full object-cover" data-testid="o-nas-photo" />
        </div>
      </Reveal>
      <div>
        <Reveal>
          <h2 className="font-display text-3xl font-extrabold uppercase text-pearl sm:text-4xl">Kim jesteśmy</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-6 space-y-5 leading-relaxed text-stonemute">
            <p>
              AD Brikman Usługi Brukarskie to firma z Łęcznej, działająca na terenie województwa lubelskiego — głównie w Łęcznej, Świdniku, Lublinie i okolicach.
            </p>
            <p>
              Układamy kostkę brukową, płyty tarasowe, płyty ceramiczne, płyty chodnikowe, palisady i obrzeża. Realizujemy podjazdy, parkingi, opaski, tarasy, chodniki oraz nawierzchnie pod garaże i altany — na obiektach prywatnych, a także na rzecz firm i administratorów nieruchomości.
            </p>
            <p>
              Wykonujemy brukarstwo artystyczne, pomagamy w wyborze i modyfikacji projektu, doradzamy oraz pomagamy w doborze i zakupie materiałów. Prace wykonujemy od A do Z lub w zakresie preferowanym przez klienta.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href={`tel:${COMPANY.phoneRaw}`} data-testid="o-nas-call" className="btn-amber px-7 py-3.5 font-mono text-xs font-medium uppercase tracking-[0.2em]">
              {COMPANY.phone}
            </a>
            <Link to="/realizacje" data-testid="o-nas-realizacje" className="btn-ghost px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em]">
              Zobacz realizacje
            </Link>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="border-y border-white/10 bg-graph grain" data-testid="o-nas-values">
      <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8">
        <SectionHead eyebrow="Nasze wartości" title="Czym się cechujemy" />
        <div className="grid gap-4 md:grid-cols-3">
          {[
            { t: "Perfekcjonizm", d: "Detal ma znaczenie: linie, fuga, spadki, czysty teren po zakończeniu prac." },
            { t: "Profesjonalizm", d: "Solidna podbudowa, odwodnienie i doradztwo na każdym etapie inwestycji." },
            { t: "Rzetelność", d: "Dotrzymujemy ustaleń, terminów i zakresu — bez niedomówień." },
          ].map((v, i) => (
            <Reveal key={v.t} delay={i * 0.1}>
              <div className="card-edge h-full p-8" data-testid={`value-${v.t.toLowerCase()}`}>
                <span className="font-mono text-sm text-amber">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-display mt-4 text-2xl font-extrabold uppercase text-pearl">{v.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-stonemute">{v.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-8" data-testid="o-nas-region">
      <SectionHead eyebrow="Lokalnie" title="Twoja okolica, nasz teren" sub={`Pracujemy w całym województwie lubelskim, m.in. w: ${CITIES.join(", ")} i okolicach.`} />
      <Reveal>
        <Link to="/kontakt" data-testid="o-nas-cta" className="btn-amber inline-block px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em]">
          Umów bezpłatną wizję lokalną
        </Link>
      </Reveal>
    </section>
  </>
);

export default ONas;
